import { useContext, useEffect, useState } from "react";
import GenericModal from "../../../../components/Modals/genericModal";
import { TextLibraryContext, MetaforceContext, WebEditorContext } from "../../../../contexts";
import * as Constants from "../../../../utils/constants";
import { MagnifyingGlassIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { useToastAction } from "../../../../hooks/useToastAction";
import { getTextLibraryLanguages, searchTextLibrary } from "../../../../api";
import _ from "lodash";
import Pagination from "../../../../components/Pagination";
import { useTranslation } from 'react-i18next';
import { resources, translations } from "../../../../translations/translationsConstants";


export default function TextLibraryDialog() {
    const { startParameter } = useContext(MetaforceContext);
    const { textLibraryDialogOpen, setTextLibraryDialogOpen, setTemplateTextValueSelectedInDialog } = useContext(TextLibraryContext);
    const { editorFileRef } = useContext(WebEditorContext);
    const [languageOptions, setLanguageOptions] = useState([]);
    const maxTextCountShownInPage = 10;
    const [searchRequest, setSearchRequest] = useState({
        startParameter: startParameter,
        groupId: "",
        groupName: "",
        cultureInfoName: "",
        languageId: "",
        validFrom: "",
        validTo: "",
        name: "",
        sortProperty: 0,
        sortOrder: 0,
        skip: 0,
        take: maxTextCountShownInPage,
    })

    const [searchResult, setSearchResult] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 0,
        pages: [0],
        pageSize: maxTextCountShownInPage,
        maxPages: Constants.paginationMaxPages,
        totalItems: 0,
        totalPages: 0,
        startIndex: 0,
    });
    const { t: te } = useTranslation([resources.edit]);
    const { t: terr } = useTranslation([resources.errors]);

    const loadAction = useToastAction();
    const searchAction = useToastAction();

    const handleLanguages = (languagesResult) => {
        languagesResult.forEach(language => {
            if (language.Name === 'Danish') {
                language.Name = te(translations.edit.library.danish);
            } else if (language.Name === 'English') {
                language.Name = te(translations.edit.library.english);
            } else if (language.Name === 'Finnish') {
                language.Name = te(translations.edit.library.finnish);
            } else if (language.Name === 'Norwegian') {
                language.Name = te(translations.edit.library.norwegian);
            } else if (language.Name === 'Swedish') {
                language.Name = te(translations.edit.library.swedish);
            }
        });
        return languagesResult;
    }

    const loadData = async () => {
        loadAction.execute(async () => {
            const languagesResult = await getTextLibraryLanguages();

            const languages = handleLanguages(languagesResult);
            setLanguageOptions(languages);

            let languageId = languages[0].Value;
            if (editorFileRef.current.CultureInfo) {
                var language = languages.find(x => x.CultureInfoName.toLowerCase() === editorFileRef.current.CultureInfo.toLowerCase());
                if (language) {
                    languageId = language.Value;
                }
            }

            setSearchRequest((prevState) => ({
                ...prevState,
                languageId: languageId,
            }));
        }, terr(translations.errors.failLoadData));
    }

    useEffect(() => {
        if (textLibraryDialogOpen) {
            loadData()
        }
    }, [textLibraryDialogOpen]);

    const handleOpen = (value) => {
        setTextLibraryDialogOpen(value);

        if (!value) {
            handleCancel();
        }
    }

    const handleConfirm = () => {
        setTextLibraryDialogOpen(false);
    }

    const handleCancel = () => {
        setTextLibraryDialogOpen(false);
    }

    const handleInputTextChange = (e) => {
        setSearchRequest((prevState) => ({
            ...prevState,
            'name': e.target.value,
        }))

        if (e.target.value === 'Cancel') {
            handleCancel();
        }
    }

    const handleInputTextSearch = async () => {
        const request = _.cloneDeep(searchRequest);
        request.skip = 0;
        request.take = maxTextCountShownInPage;
        setSearchRequest(request);

        await searchTexts(request);
    }

    const handleLanguageSelectChange = async (e) => {
        const request = _.cloneDeep(searchRequest);
        request.skip = 0;
        request.take = maxTextCountShownInPage;
        request.languageId = e.target.value;
        setSearchRequest(request);

        await searchTexts(request);
    }

    const handlePaginationChange = async (newPagination) => {
        setPagination(newPagination);
        const newSearchRequest = _.cloneDeep(searchRequest);
        newSearchRequest.skip = newPagination.startIndex;
        setSearchRequest(newSearchRequest);

        await searchTexts(newSearchRequest, newPagination);
    }

    const searchTexts = async (newSearchRequest, newPagination) => {
        searchAction.execute(async () => {
            const res = await searchTextLibrary(newSearchRequest);

            setSearchResult(res);

            const clonedPagination = _.cloneDeep(newPagination ? newPagination : pagination);
            setPagination({
                ...clonedPagination,
                totalItems: res.Count,
                totalPages: Math.ceil(res.Count / clonedPagination.pageSize),
                currentPage: newSearchRequest.skip === 0 ? 0 : clonedPagination.currentPage,
            })
        }, terr(translations.errors.failSearch));
    }

    const handleTextClick = (templateText) => {
        setTextLibraryDialogOpen(false);

        const templateTextValue = templateText.Values.find(x => x.LanguageId === searchRequest.languageId);
        if (templateTextValue) {
            setTemplateTextValueSelectedInDialog(templateTextValue);
        }
    }

    return (
        <GenericModal
            open={textLibraryDialogOpen}
            setOpen={handleOpen}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
            showCancelButton={false}
            showConfirmButton={false}
            title={te(translations.edit.library.insertTextFromLibrary)} >
            <div className="tw-w-full tw-pt-5 tw-text-left">
                <div className="tw-grid tw-grid-cols-6 tw-gap-4">
                    <div className="tw-col-span-4">
                        <div className="tw-flex tw-justify-start tw-relative tw-rounded-md tw-w-full">
                            <MagnifyingGlassIcon className="tw-absolute tw-top-0 tw-left-0 tw-bottom-0 tw-h-5 tw-m-auto tw-mx-2 tw-text-gray-400 tw-ml-3" />
                            <input
                                aria-label={te(translations.edit.search)}
                                type={"text"}
                                className={`tw-px-3 tw-py-2 tw-w-full focus:tw-ring-indigo-700 focus:tw-border-indigo-700 tw-block sm:tw-text-sm tw-border tw-border-gray-300 tw-border-solid tw-rounded-md tw-pl-10`}
                                onChange={handleInputTextChange}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleInputTextSearch();
                                    }
                                }}
                                placeholder={te(translations.edit.search)}
                            />
                        </div>
                    </div>
                    <div className="tw-col-span-2">
                        <select
                            aria-label={te(translations.edit.library.selectLanguage)}
                            value={searchRequest.languageId}
                            className="tw-px-3 tw-py-2 tw-h-full tw-w-full sm:tw-text-sm focus:tw-ring-indigo-700 focus:tw-border-indigo-700 tw-border-gray-300 tw-rounded-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
                            onChange={handleLanguageSelectChange}
                        >
                            {languageOptions.map((language, index) => (
                                <option key={index} value={language.Value}>{language.Name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {searchResult && (
                    <>
                        <div className="tw-text-sm tw-font-medium tw-text-indigo-700 tw-mt-7 tw-mb-2">
                            {searchResult.Count === 0
                                ?
                                `0 ${te(translations.edit.library.texts)}`
                                :
                                `${searchResult.Count} ${te(translations.edit.library.texts)}, ${te(translations.edit.library.page)} ${pagination.currentPage + 1} ${te(translations.edit.library.of)} ${pagination.totalPages}`}
                        </div>

                        <div className="tw-overflow-x-auto tw-overflow-y-auto tw-align-middle tw-inline-block tw-min-w-full" >
                            <div className="tw-shadow-sm tw-border tw-border-solid tw-border-gray-300 tw-bg-white">
                                <div className="tw-flex tw-flex-col tw-min-w-full tw-divide-y tw-divide-gray-300">
                                    {searchResult?.Texts.map((templateText) => (
                                        <button
                                            key={templateText.Id}
                                            type="button"
                                            className="tw-bg-white tw-border-0 tw-rounded-none t-w-full hover:tw-bg-gray-50 tw-text-gray-400 hover:tw-text-gray-600 tw-flex tw-flex-row tw-px-2 sm:tw-px-6 tw-py-3"
                                            onClick={() => handleTextClick(templateText)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleTextClick(templateText);
                                                }
                                            }}
                                        >
                                            <span className="tw-whitespace-nowrap tw-font-medium tw-text-sm tw-text-gray-900 tw-text-left tw-flex-1">{templateText.Name}</span>
                                            <PlusCircleIcon className="tw-h-5 tw-w-5" aria-hidden="true" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <Pagination
                            pagination={pagination}
                            disabled={searchAction.isExecuting}
                            onPaginationChange={(e) => handlePaginationChange(e)}
                            showPageIndex={false}
                        />
                    </>
                )}
            </div>
        </GenericModal>
    )
}